import ApiService from '@/data/services/ApiService'
import { AxiosResponse } from 'axios'
import { IServiceResponse, IUser } from '@/data/models'

export class AuthApiService extends ApiService {
  /**
   * Login request
   *
   * @param username string
   * @param password string
   */
  static async login(username: string, password: string): Promise<IServiceResponse<IUser>> {
    return this.axios.post('auth/login', {
        username: username,
        password: password
      }
    ).then((response: AxiosResponse<IServiceResponse<IUser>>) => response.data)
  }

  /**
   * Logout request
   */
  static async logout(): Promise<AxiosResponse<undefined>> {
    return this.axios.post('auth/logout')
  }

  /**
   * Fetch authenticated user data request
   */
  static async me(): Promise<IServiceResponse<IUser>> {
    return this.axios.get('auth/me')
      .then((response: AxiosResponse<IServiceResponse<IUser>>) => response.data)
  }

  static async initCsrf(): Promise<AxiosResponse<undefined>> {
    return this.axios.get('auth/csrf')
      .then((response: AxiosResponse<AxiosResponse<undefined>>) => response.data)
  }

  static async resetPassword(data: object): Promise<AxiosResponse> {
    return this.axios.post('auth/reset-password', data)
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async changePassword(data: object): Promise<AxiosResponse> {
    return this.axios.post('auth/change-password', data)
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async forgotPassword(data: object): Promise<AxiosResponse> {
    return this.axios.post('auth/forgot-password', data)
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async deletePolarTeamIntegration(polarTeamIntegrationId: string): Promise<AxiosResponse> {
    return this.axios.delete('integrations/polar-team', {
      params: {
        polar_team_integration_id: polarTeamIntegrationId
      }
    })
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async getGarminAuthUrl(params: object): Promise<AxiosResponse> {
    return this.axios.get('integrations/garmin/oauth-url', {
      params: params
    })
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async addGpexeIntegration(data: object): Promise<AxiosResponse> {
    return this.axios.post('integrations/gpexe/connect', data)
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async deleteGpexeIntegration(gpexeIntegrationId: string): Promise<AxiosResponse> {
    return this.axios.delete('integrations/gpexe', {
      params: {
        gpexe_integration_id: gpexeIntegrationId
      }
    })
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async addFieldwizIntegration(data: object): Promise<AxiosResponse> {
    return this.axios.post('integrations/fieldwiz/connect', data)
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async deleteFieldwizIntegration(fieldwizIntegrationId: string): Promise<AxiosResponse> {
    return this.axios.delete('integrations/fieldwiz', {
      params: {
        fieldwiz_integration_id: fieldwizIntegrationId
      }
    })
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async addCatapultIntegration(data: object): Promise<AxiosResponse> {
    return this.axios.post('integrations/catapult/connect', data)
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async deleteCatapultIntegration(catapultIntegrationId: string): Promise<AxiosResponse> {
    return this.axios.delete('integrations/catapult', {
      params: {
        catapult_integration_id: catapultIntegrationId
      }
    })
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async addValdIntegration(data: object): Promise<AxiosResponse> {
    return this.axios.post('integrations/vald/connect', data)
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async updateValdIntegration(data: object): Promise<AxiosResponse> {
    return this.axios.post('integrations/vald/update', data)
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async deleteValdIntegration(valdIntegrationId: string): Promise<AxiosResponse> {
    return this.axios.delete('integrations/vald', {
      params: {
        vald_integration_id: valdIntegrationId
      }
    })
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async addKinexonIntegration(data: object): Promise<AxiosResponse> {
    return this.axios.post('integrations/kinexon/connect', data)
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async deleteKinexonIntegration(kinexonIntegrationId: string): Promise<AxiosResponse> {
    return this.axios.delete('integrations/kinexon', {
      params: {
        kinexon_integration_id: kinexonIntegrationId
      }
    }).then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async addCometIntegration(data: object): Promise<AxiosResponse> {
    return this.axios.post('integrations/comet/connect', data)
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async deleteCometIntegration(cometIntegrationId: string): Promise<AxiosResponse> {
    return this.axios.delete('integrations/comet', {
      params: {
        comet_integration_id: cometIntegrationId
      }
    }).then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async addStatsportsIntegration(data: object): Promise<AxiosResponse> {
    return this.axios.post('integrations/statsports/connect', data)
      .then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async deleteStatsportsIntegration(statsportsIntegrationId: string): Promise<AxiosResponse> {
    return this.axios.delete('integrations/statsports', {
      params: {
        statsports_integration_id: statsportsIntegrationId
      }
    }).then((response: AxiosResponse<AxiosResponse>) => response.data)
  }

  static async discoverRegion(email: string): Promise<AxiosResponse> {
    return this.axios.get('region/discover', {
      params: {
        email: email
      }
    })
      .then((response: AxiosResponse<AxiosResponse>) => {
        const region = response.data?.data?.region || null

        if (region) {
          ApiService.axios.defaults.headers.common['X-Ultrax-User-Region'] = region
          window.localStorage.setItem('ultrax_user_region', region)
        }

        return response.data
      })
  }
}
